import { apiClient } from "./client";

// Backend response interface
interface BackendAuthResponse {
  _id: string;
  access_token: string;
  datetime_created: string;
  email: string;
  favorited_listings: string[];
  first_name: string;
  hashed_password: string;
  instagram_username: string | null;
  last_login: string | null;
  last_name: string;
  token_type: string;
}

export interface User {
  _id: string;
  email: string;
  first_name: string;
  last_name: string;
  instagram_username?: string | null;
  last_login: string | null;
  datetime_created: string;
  favorited_listings: string[];
  accessToken: string;
  token_type: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  remember?: boolean;
}

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  newPassword: string;
  confirmPassword: string;
  instagram_username?: string;
  marketingOptIn?: boolean;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  password: string;
  confirmPassword: string;
}

export interface ProfileUpdateRequest {
  email: string;
  firstName?: string;
  lastName?: string;
  instagram?: string;
  newPassword?: string;
  currentPassword: string;
}

export interface AuthResponse {
  success: boolean;
  data?: User;
  error?: string;
  message?: string;
}

export interface GoogleAuthRequest {
  credential: string;
}

class AuthService {
  private mapBackendResponseToUser(response: BackendAuthResponse): User {
    return {
      _id: response._id,
      email: response.email,
      first_name: response.first_name,
      last_name: response.last_name,
      instagram_username: response.instagram_username,
      last_login: response.last_login,
      datetime_created: response.datetime_created,
      favorited_listings: response.favorited_listings,
      accessToken: response.access_token,
      token_type: response.token_type,
    };
  }

  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<BackendAuthResponse>("/login", credentials);
      const userData = this.mapBackendResponseToUser(response);

      apiClient.setToken(response.access_token);
      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("access_token", response.access_token);

      return { success: true, data: userData };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Login failed",
      };
    }
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<BackendAuthResponse>("/register", userData);
      const user = this.mapBackendResponseToUser(response);

      apiClient.setToken(response.access_token);
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("access_token", response.access_token);

      return { success: true, data: user };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Registration failed",
      };
    }
  }

  async forgotPassword(data: ForgotPasswordRequest): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<{ message: string }>("/forgot-password", data);
      return { success: true, message: response.message };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to send reset email",
      };
    }
  }

  async resetPassword(data: ResetPasswordRequest, token: string): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<{ message: string }>(`/reset-password/${token}`, data);
      return { success: true, message: response.message };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to reset password",
      };
    }
  }

  async logout() {
    apiClient.setToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("access_token");
  }

  async googleAuth(credential: string): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<BackendAuthResponse>("/google", { credential });
      const userData = this.mapBackendResponseToUser(response);

      apiClient.setToken(response.access_token);
      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("access_token", response.access_token);

      return { success: true, data: userData };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Google authentication failed",
      };
    }
  }

  async updateProfile(data: ProfileUpdateRequest): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<BackendAuthResponse>("/modify-profile", data);
      const userData = this.mapBackendResponseToUser(response);
      localStorage.setItem("user", JSON.stringify(userData));
      return { success: true, data: userData };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to update profile",
      };
    }
  }

  getStoredUser(): User | null {
    if (typeof window === "undefined") return null;
    try {
      const userData = localStorage.getItem("user");
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  }

  getStoredToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("access_token");
  }

  isAuthenticated(): boolean {
    return !!this.getStoredToken();
  }

  initializeAuth(): void {
    const token = this.getStoredToken();
    if (token) {
      apiClient.setToken(token);
    }
  }
}

export const authService = new AuthService();
